import React, { Component } from "react";
import auth from "../../services/authService";
import ProposalForm from "../Form/proposalForm";
import ProposalConfirm from "../Form/proposalConfirm";
import ProposalSuccess from "../Form/proposalSuccess";
import { v4 as uuidv4 } from "uuid";

export class FusionProposal extends Component {
  _isMounted = true;
  proposalFormRef = React.createRef();

  state = {
    // Basic fusion settings
    rebalanceType: "Automatic", // Manual or Automatic
    originalRebalanceType: "Automatic", // Store original value before forcing manual
    totalFusionAmount: 0,

    // Proposal list management
    selectedProposals: [], // Array of proposals with amount and percentage
    showProposalDialog: false,
    availableProposals: [], // All non-fusion proposals from details forms
    ispSeedInitialized: false,
    seededFromISPWorkflow: false,
    originalISPStrategyNames: [],

    // Dialog state for copy functionality
    dialogFormRequestType: "NewSP", // "NewSP" for new, "Copy" for copying, "Edit" for editing
    dialogEditProposal: null, // Proposal data to copy when copying
    editIndex: -1, // Index of proposal being edited, -1 means no edit

    // Alert dialog state
    showAlertDialog: false,
    alertTitle: "",
    alertMessage: "",

    // Confirm dialog state
    showConfirmDialog: false,
    confirmTitle: "",
    confirmMessage: "",
    confirmCallback: null,

    // Validation states
    totalPercentage: 0,
    isValid: false,
    errors: {},

    // Confirmation mode
    showConfirmation: false,
    confirmationData: null,

    // Success screen
    showSuccess: false,
  };

  componentDidMount = () => {
    if (this._isMounted) {
      this.checkFusionDataAndReset();
      this.loadTotalFusionAmount();
      this.loadAvailableProposals();
      this.initializeISPSeedIfNeeded();
    }
  };

  componentDidUpdate = () => {
    this.initializeISPSeedIfNeeded();
  };

  // Alert dialog methods
  showAlert = (title, message) => {
    // Prevent background scrolling
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = "17px"; // Account for scrollbar width

    this.setState({
      showAlertDialog: true,
      alertTitle: title,
      alertMessage: message,
    });
  };

  hideAlert = () => {
    // Restore background scrolling
    document.body.style.overflow = "auto";
    document.body.style.paddingRight = "0px";

    this.setState({
      showAlertDialog: false,
      alertTitle: "",
      alertMessage: "",
    });
  };

  // Confirm dialog methods
  showConfirm = (title, message, callback) => {
    // Prevent background scrolling
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = "17px"; // Account for scrollbar width

    this.setState({
      showConfirmDialog: true,
      confirmTitle: title,
      confirmMessage: message,
      confirmCallback: callback,
    });
  };

  hideConfirm = () => {
    // Restore background scrolling
    document.body.style.overflow = "auto";
    document.body.style.paddingRight = "0px";

    this.setState({
      showConfirmDialog: false,
      confirmTitle: "",
      confirmMessage: "",
      confirmCallback: null,
    });
  };

  handleConfirmYes = () => {
    const { confirmCallback } = this.state;
    this.hideConfirm();
    if (confirmCallback) {
      confirmCallback();
    }
  };

  // Check if fusion data is present, if not reset and go back
  checkFusionDataAndReset = () => {
    const { fusionStepGuideData } = this.props;

    // Check if we have essential fusion data
    if (!fusionStepGuideData || !fusionStepGuideData.assetValue) {
      this.handleReset();
      return;
    }
  };

  componentWillUnmount = () => {
    this._isMounted = false;
    // Ensure body scrolling is restored if component unmounts with modal open
    document.body.style.overflow = "auto";
    document.body.style.paddingRight = "0px";
  };

  // Load the total fusion amount from the form details
  loadTotalFusionAmount = () => {
    const { fusionStepGuideData } = this.props;
    if (fusionStepGuideData && fusionStepGuideData.assetValue) {
      const amount =
        parseFloat(fusionStepGuideData.assetValue.replace(/[^0-9.-]+/g, "")) ||
        0;
      this.setState({ totalFusionAmount: amount });
    }
  };

  // Load available proposals by filtering through proposal types
  loadAvailableProposals = () => {
    const { lists } = this.props;
    if (!lists) return;
    // Use the proposal-facing list (excludes ISP-only strategies).
    const sourceTypes = lists.proposalTypesForProposals || lists.proposalTypes;
    if (!sourceTypes) return;

    // Filter out fusion proposals and create available proposal list
    const available = sourceTypes
      .filter((proposal) => !proposal.name.toLowerCase().includes("fusion"))
      .map((proposal) => ({
        id: proposal.name,
        name: proposal.name,
        clientName: "", // Will be filled when selected
        advisorName: "", // Will be filled when selected
        amount: 0,
        percentage: 0,
      }));

    this.setState({ availableProposals: available });
  };

  getBlankRegistrations = () => [
    {
      registration: "registration0",
      value: "",
      percentage: "100",
    },
  ];

  getSeededRegistrations = () => {
    const fromStepGuide = this.props.fusionStepGuideData?.registrations;
    if (Array.isArray(fromStepGuide) && fromStepGuide.length > 0) {
      return JSON.parse(JSON.stringify(fromStepGuide));
    }

    const fromEditProposal = this.props.editProposal?.registrations;
    if (Array.isArray(fromEditProposal) && fromEditProposal.length > 0) {
      return JSON.parse(JSON.stringify(fromEditProposal));
    }

    const registrationValue =
      this.props.editProposal?.REGISTRATION ||
      this.props.editProposal?.registration ||
      "";

    if (registrationValue) {
      return [
        {
          registration: "registration0",
          value: registrationValue,
          percentage: "100",
        },
      ];
    }

    return this.getBlankRegistrations();
  };

  getFusionTotalAmount = () => {
    const { fusionStepGuideData } = this.props;
    return (
      parseFloat(fusionStepGuideData?.assetValue?.replace(/[^0-9.-]+/g, "")) || 0
    );
  };

  hasISPSeedSource = () => {
    return (
      Array.isArray(this.props.ispReferenceFusionAllocations) &&
      this.props.ispReferenceFusionAllocations.length > 0 &&
      this.getFusionTotalAmount() > 0
    );
  };

  getPendingInitialReviewCount = (proposals = this.state.selectedProposals) => {
    return proposals.filter(
      (proposal) => proposal.seededFromISP && !proposal.initialReviewComplete
    ).length;
  };

  isInInitialReviewMode = () => {
    return (
      this.state.seededFromISPWorkflow &&
      this.getPendingInitialReviewCount() > 0
    );
  };

  getOriginalISPClientName = () => {
    return this.getClientNameFromFormDetails();
  };

  getISPReferenceRiskCategory = () => {
    return this.props.editProposal?.ispRiskCategory || "";
  };

  getISPReferenceSelectedPortfolio = () => {
    return (
      this.props.editProposal?.ispSelectedPortfolio ||
      this.props.editProposal?.["RISK TOLERANCE"] ||
      ""
    );
  };

  getStrategyDivergenceWarnings = () => {
    if (
      !this.state.seededFromISPWorkflow ||
      this.isInInitialReviewMode() ||
      this.state.originalISPStrategyNames.length === 0
    ) {
      return [];
    }

    const originalStrategies = new Set(
      this.state.originalISPStrategyNames.map((name) => this.normalizeText(name))
    );

    return [
      ...new Set(
        this.state.selectedProposals
          .map((proposal) => proposal.name)
          .filter(Boolean)
          .filter((name) => !originalStrategies.has(this.normalizeText(name)))
      ),
    ];
  };

  validateProposalAmount = (programName, amount) => {
    const numericAmount = Math.round(parseFloat(amount) || 0);
    const programLimits = this.getProgramLimits(programName);

    if (programLimits && numericAmount > 0) {
      if (numericAmount > programLimits.max) {
        return `Maximum allowed: $${programLimits.max.toLocaleString()}`;
      }
      if (numericAmount < programLimits.min) {
        return `Minimum required: $${programLimits.min.toLocaleString()}`;
      }
    }

    return null;
  };

  prepareProposalData = (
    proposalData,
    { preserveBlankRiskTolerance = false } = {}
  ) => {
    let enhancedProposalData = { ...proposalData };

    const matchingType = this.props.lists?.proposalTypes?.find(
      (pt) => pt.name === proposalData.program
    );

    if (matchingType) {
      if (matchingType.powerPoint) {
        enhancedProposalData.powerPoint = matchingType.powerPoint;
      }

      if (matchingType.paperwork && matchingType.paperwork.factSheet) {
        enhancedProposalData.paperwork = {
          ...enhancedProposalData.paperwork,
          factSheet: matchingType.paperwork.factSheet,
        };
      }
    }

    if (enhancedProposalData.advisorName && this.props.lists?.advisors) {
      const matchingAdvisor = this.props.lists.advisors.find(
        (advisor) => advisor.Name === enhancedProposalData.advisorName
      );

      if (matchingAdvisor) {
        if (!enhancedProposalData.logo) {
          enhancedProposalData.logo = enhancedProposalData.advisorName;
        }
        enhancedProposalData.logoSize = matchingAdvisor.LogoSize;
        enhancedProposalData.logoOffset = matchingAdvisor.LogoOffset;
      }
    }

    if (
      !preserveBlankRiskTolerance &&
      (!enhancedProposalData.riskTolerance ||
        enhancedProposalData.riskTolerance.trim() === "")
    ) {
      if (matchingType && matchingType.riskScore) {
        const riskOptions = Object.keys(matchingType.riskScore);
        if (riskOptions.length > 0) {
          enhancedProposalData.riskTolerance = riskOptions[0];
        }
      }

      if (
        !enhancedProposalData.riskTolerance ||
        enhancedProposalData.riskTolerance.trim() === ""
      ) {
        enhancedProposalData.riskTolerance = "Moderate";
      }
    }

    return enhancedProposalData;
  };

  createProposalEntry = (
    proposalData,
    { preserveBlankRiskTolerance = false, metadata = {} } = {}
  ) => {
    const enhancedProposalData = this.prepareProposalData(proposalData, {
      preserveBlankRiskTolerance,
    });
    const formDataCopy = JSON.parse(JSON.stringify(enhancedProposalData));
    const numericAmount =
      proposalData.fusionAmount ||
      parseFloat(proposalData.assetValue?.replace(/[^0-9.-]+/g, "")) ||
      0;

    return {
      id:
        metadata.id ||
        `${proposalData.program}_${Date.now()}_${Math.random()
          .toString(36)
          .slice(2, 7)}`,
      name: proposalData.program,
      clientName: proposalData.clientName,
      advisorName: proposalData.advisorName,
      customTemplate: proposalData.customTemplate || "",
      amount: numericAmount,
      percentage: proposalData.fusionPercentage || 0,
      amountError: this.validateProposalAmount(proposalData.program, numericAmount),
      seededFromISP: metadata.seededFromISP || false,
      initialReviewComplete:
        metadata.initialReviewComplete !== undefined
          ? metadata.initialReviewComplete
          : true,
      formData: {
        ...formDataCopy,
        proposalType: formDataCopy.proposalType || formDataCopy.program,
        customTemplate: formDataCopy.customTemplate || "",
      },
    };
  };

  initializeISPSeedIfNeeded = () => {
    if (
      !this._isMounted ||
      this.state.ispSeedInitialized ||
      !this.hasISPSeedSource()
    ) {
      return;
    }

    const allocations = this.props.ispReferenceFusionAllocations;
    const totalAmount = this.getFusionTotalAmount();
    let runningAmount = 0;

    const seededProposals = allocations.map((allocation, index) => {
      const percentage =
        Math.round((parseFloat(allocation.percentage) || 0) * 100) / 100;
      const amount =
        index === allocations.length - 1
          ? Math.max(totalAmount - runningAmount, 0)
          : Math.round((percentage / 100) * totalAmount);
      runningAmount += amount;

      const seedData = {
        program: allocation.strategy || "",
        clientName: this.getOriginalISPClientName(),
        advisorName: this.getAdvisorNameFromFormDetails(),
        customTemplate: this.getCustomTemplateFromFormDetails(),
        riskTolerance: "",
        papercheck: "true",
        othercheck: "",
        incomeNeeds: "",
        optionsTM: this.props.fusionStepGuideData?.optionsTM || "",
        assetValue: `$${Math.round(amount).toLocaleString()}`,
        logo: "",
        logoSize: "",
        logoOffset: "",
        insertType: this.props.fusionStepGuideData?.insertType || "",
        fusionPercentage: percentage,
        fusionAmount: amount,
        isNewFusionClient:
          this.props.fusionStepGuideData?.isNewFusionClient !== undefined
            ? this.props.fusionStepGuideData.isNewFusionClient
            : true,
        fusionAccountNumbers: Array.isArray(
          this.props.fusionStepGuideData?.fusionAccountNumbers
        )
          ? JSON.parse(
              JSON.stringify(this.props.fusionStepGuideData.fusionAccountNumbers)
            )
          : [],
        registrations: this.getSeededRegistrations(),
      };

      return this.createProposalEntry(seedData, {
        preserveBlankRiskTolerance: true,
        metadata: {
          seededFromISP: true,
          initialReviewComplete: false,
        },
      });
    });

    const shouldForceManual =
      this.checkForceManualRebalance(seededProposals);

    this.setState(
      (prevState) => ({
        selectedProposals: seededProposals,
        ispSeedInitialized: true,
        seededFromISPWorkflow: true,
        originalISPStrategyNames: [
          ...new Set(
            allocations.map((allocation) => allocation.strategy).filter(Boolean)
          ),
        ],
        rebalanceType: shouldForceManual
          ? "Manual"
          : prevState.originalRebalanceType,
      }),
      this.calculateTotals
    );
  };

  // Handle manual/automatic selection
  handleRebalanceTypeChange = (type) => {
    this.setState({
      rebalanceType: type,
      originalRebalanceType: type, // Store user's choice
    });
  };

  // Show the proposal selection dialog
  showProposalDialog = () => {
    // Prevent background scrolling
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = "17px"; // Account for scrollbar width

    this.setState({
      showProposalDialog: true,
      dialogFormRequestType: "NewSP",
      dialogEditProposal: null,
      editIndex: -1,
    });
  };

  // Hide the proposal selection dialog
  hideProposalDialog = () => {
    // Restore background scrolling
    document.body.style.overflow = "auto";
    document.body.style.paddingRight = "0px";

    this.setState({
      showProposalDialog: false,
      dialogFormRequestType: "NewSP",
      dialogEditProposal: null,
      editIndex: -1,
    });
  };

  // Add a proposal to the fusion list - receives data from the form
  addProposal = (proposalData) => {
    // Check if this is an edit operation using editIndex
    const isEditing = this.state.editIndex !== -1;

    // For new proposals (not edits), check the max limit
    if (!isEditing && this.state.selectedProposals.length >= 8) {
      this.setState({
        errors: {
          ...this.state.errors,
          maxProposals: "Maximum of 8 proposals allowed",
        },
      });
      return;
    }
    const existingProposal = isEditing
      ? this.state.selectedProposals[this.state.editIndex]
      : null;
    const proposalToSave = this.createProposalEntry(proposalData, {
      metadata: existingProposal
        ? {
            id: existingProposal.id,
            seededFromISP: !!existingProposal.seededFromISP,
            initialReviewComplete: existingProposal.seededFromISP
              ? true
              : existingProposal.initialReviewComplete !== false,
          }
        : {
            seededFromISP: false,
            initialReviewComplete: true,
          },
    });

    this.setState((prevState) => {
      let updatedProposals;

      if (isEditing) {
        // Replace the existing proposal at editIndex
        updatedProposals = [...prevState.selectedProposals];
        updatedProposals[prevState.editIndex] = proposalToSave;
      } else {
        // Add new proposal
        updatedProposals = [...prevState.selectedProposals, proposalToSave];
      }

      // Check if we should force manual rebalance based on validation conditions
      const shouldForceManual =
        this.checkForceManualRebalance(updatedProposals);

      // Restore background scrolling when closing dialog
      document.body.style.overflow = "auto";
      document.body.style.paddingRight = "0px";

      return {
        selectedProposals: updatedProposals,
        errors: { ...prevState.errors, maxProposals: null },
        showProposalDialog: false, // Close the dialog after adding/editing
        dialogFormRequestType: "NewSP", // Reset to new form
        dialogEditProposal: null, // Clear any edit/copy data
        editIndex: -1, // Reset edit index
        // Use original rebalance type if not forced, otherwise force manual
        rebalanceType: shouldForceManual
          ? "Manual"
          : prevState.originalRebalanceType,
      };
    }, this.calculateTotals);
  };

  // Check if we should force manual rebalance based on validation conditions
  checkForceManualRebalance = (proposals) => {
    if (proposals.length <= 1) {
      return false; // No need to force manual for single proposal
    }

    // Check for different client names
    const clientNames = [
      ...new Set(
        proposals.map((p) => p.clientName?.trim().toLowerCase()).filter(Boolean)
      ),
    ];
    if (clientNames.length > 1) {
      return true; // Different client names found
    }

    // Get registration and group values for each proposal
    const registrationData = proposals.map((proposal) => {
      return this.getProposalRegistrationGroup(proposal);
    });

    // Extract just the group values
    const groupValues = registrationData.map(
      ([regName, groupValue]) => groupValue
    );

    // Extract registration names
    const registrationNames = registrationData.map(
      ([regName, groupValue]) => regName
    );

    // Count how many have null group values
    const nullGroupCount = groupValues.filter((g) => g === null).length;

    // Get unique registration names (to check if all are same registration type)
    const uniqueRegistrations = [
      ...new Set(registrationNames.filter((r) => r !== null)),
    ];

    // Get unique group values (excluding nulls)
    const uniqueNonNullGroups = [
      ...new Set(groupValues.filter((g) => g !== null)),
    ];

    // If all registrations are the same type (or all null), and all groups are the same, allow user control
    if (
      uniqueRegistrations.length <= 1 &&
      uniqueNonNullGroups.length === 0 &&
      nullGroupCount === proposals.length
    ) {
      // All have same registration (or no registration) and all have null groups
      return false;
    }

    if (uniqueNonNullGroups.length === 1 && nullGroupCount === 0) {
      // All have the same non-null group value
      return false;
    }

    // Force manual if:
    // 1. proposals >= 2 AND null group count > 1 AND different registrations exist
    if (
      proposals.length >= 2 &&
      nullGroupCount > 1 &&
      uniqueRegistrations.length > 1
    ) {
      return true; // Multiple null groups with different registration types
    }

    // 2. Mix of null and non-null groups, or multiple different non-null groups
    if (
      (nullGroupCount > 0 && uniqueNonNullGroups.length > 0) ||
      uniqueNonNullGroups.length > 1
    ) {
      return true; // Different group values found
    }

    return false;
  };

  // Get registration type (Qualified/Non-Qualified) for a proposal
  getProposalRegistrationType = (proposal) => {
    if (!proposal.formData?.registrations) {
      return null;
    }

    // Find the registration type in the lists - try both props locations
    const { lists } = this.props;
    let registrationTypes = lists?.registrationTypes;

    // Fallback check if registrationTypes is not in lists
    if (!registrationTypes) {
      return null;
    }

    // Extract registration value from the registrations array structure
    let registrationValue = null;

    if (Array.isArray(proposal.formData.registrations)) {
      // Handle array format: [{registration: "registration0", value: "IRA", percentage: "100"}]
      const firstRegistration = proposal.formData.registrations[0];
      registrationValue = firstRegistration?.value;
    } else if (typeof proposal.formData.registrations === "string") {
      // Handle string format directly
      registrationValue = proposal.formData.registrations;
    }

    if (!registrationValue) {
      return null;
    }

    const registrationType = registrationTypes.find((reg) => {
      return reg.name === registrationValue;
    });

    // Return the Account Type which should contain "Qualified" or "Non-Qualified"
    return registrationType?.["Account Type"] || null;
  };

  // Get registration group for a proposal
  // Returns: [registrationName, groupValue]
  // - registrationName: the registration name (e.g., "IRA", "401k") or null if no registration
  // - groupValue: the group value (string) or null if no group is set
  getProposalRegistrationGroup = (proposal) => {
    if (!proposal.formData?.registrations) {
      return [null, null]; // No registration at all
    }

    const { lists } = this.props;
    let registrationTypes = lists?.registrationTypes;

    if (!registrationTypes) {
      return [null, null]; // Can't determine registration without types list
    }

    // Extract registration value from the registrations array structure
    let registrationValue = null;

    if (Array.isArray(proposal.formData.registrations)) {
      // Handle array format: [{registration: "registration0", value: "IRA", percentage: "100"}]
      const firstRegistration = proposal.formData.registrations[0];
      registrationValue = firstRegistration?.value;
    } else if (typeof proposal.formData.registrations === "string") {
      // Handle string format directly
      registrationValue = proposal.formData.registrations;
    }

    if (!registrationValue) {
      return [null, null]; // Has registrations field but no value
    }

    const registrationType = registrationTypes.find((reg) => {
      return reg.name === registrationValue;
    });

    if (!registrationType) {
      // Registration value exists but not found in types list
      return [registrationValue, null];
    }

    // Return tuple: [registration name, group value (may be null/undefined)]
    return [registrationValue, registrationType.group || null];
  };

  // Remove a proposal from the fusion list
  removeProposal = (proposalId) => {
    // Find the proposal being removed for the confirmation message
    const proposalToRemove = this.state.selectedProposals.find(
      (p) => p.id === proposalId
    );

    if (!proposalToRemove) return;

    // Show confirmation dialog
    const message = `Are you sure you want to remove this strategy?\n\nStrategy: ${
      proposalToRemove.name
    }\nClient: ${proposalToRemove.clientName}\nAmount: $${Math.round(
      proposalToRemove.amount
    ).toLocaleString()}`;

    this.showConfirm("Remove Strategy", message, () => {
      this.setState((prevState) => {
        const updatedProposals = prevState.selectedProposals.filter(
          (p) => p.id !== proposalId
        );

        // Check if we need to allow user to change rebalance type after removal
        const shouldForceManual =
          this.checkForceManualRebalance(updatedProposals);

        return {
          selectedProposals: updatedProposals,
          // Clear any validation errors for the removed proposal
          errors: {
            ...prevState.errors,
            [`amount_${proposalId}`]: null,
          },
          // Restore original rebalance type if no longer forced, otherwise keep manual
          rebalanceType: shouldForceManual
            ? "Manual"
            : prevState.originalRebalanceType,
        };
      }, this.calculateTotals);
    });
  };

  // Edit a proposal from the fusion list (similar to copy but replaces instead of adding new)
  editProposal = (proposalIndex) => {
    const proposal = this.state.selectedProposals[proposalIndex];
    // Validate proposal data exists
    if (!proposal || !proposal.formData) {
      console.error("Cannot edit proposal: missing proposal data");
      return;
    }

    const registrationsForEdit = Array.isArray(proposal.formData.registrations)
      ? proposal.formData.registrations.length > 0
        ? JSON.parse(JSON.stringify(proposal.formData.registrations))
        : this.getBlankRegistrations()
      : proposal.formData.registrations || this.getBlankRegistrations();

    // Convert fusion proposal data to the format expected by ProposalForm
    // This maps the fusion proposal structure back to the submission format
    const editProposalData = {
      // Core identification fields
      program: proposal.name,
      "CLIENT NAMES": proposal.clientName || "",
      "ADVISOR NAME": proposal.advisorName || "",

      // Template and branding fields
      customTemplate: proposal.customTemplate || "",
      LOGO: proposal.formData.logo || proposal.advisorName || "",
      logoSize: proposal.formData.logoSize || "",
      logoOffset: proposal.formData.logoOffset || "",

      // Risk and strategy fields - ensure empty string is treated as empty
      "embedded-excel":
        proposal.formData.riskTolerance &&
        proposal.formData.riskTolerance.trim() !== ""
          ? proposal.formData.riskTolerance
          : "",
      "insert-pdf": proposal.formData.snapShot || "",
      incomeNeeds: proposal.formData.incomeNeeds || 0,
      optionsTM: proposal.formData.optionsTM || "",

      // Document options
      papercheck:
        proposal.formData.papercheck !== undefined
          ? proposal.formData.papercheck
          : "true",
      othercheck: proposal.formData.othercheck || "",
      insertType: proposal.formData.insertType || "Fact Sheet",

      // Financial fields - use current fusion amounts
      assetValue: `$${proposal.amount.toLocaleString()}`,
      fusionPercentage: parseFloat(proposal.percentage) || 0,
      fusionAmount: proposal.amount || 0,

      // Registration data (deep copy to prevent shared references)
      registrations: registrationsForEdit,

      // Include fusion account numbers when editing
      fusionAccountNumbers: proposal.formData.fusionAccountNumbers
        ? JSON.parse(JSON.stringify(proposal.formData.fusionAccountNumbers))
        : [],

      // Set isNewFusionClient based on presence of account numbers
      isNewFusionClient:
        !proposal.formData.fusionAccountNumbers ||
        proposal.formData.fusionAccountNumbers.length === 0,

      // Clear timestamp for new copy
      DateTime: "",
    };

    // Prevent background scrolling
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = "17px"; // Account for scrollbar width

    this.setState({
      showProposalDialog: true,
      dialogFormRequestType: "Edit",
      dialogEditProposal: editProposalData,
      editIndex: proposalIndex,
    });
  };

  // Copy a proposal from the fusion list for editing
  copyProposal = (proposal) => {
    // Validate proposal data exists
    if (!proposal || !proposal.formData) {
      console.error("Cannot copy proposal: missing proposal data");
      return;
    }

    const registrationsForEdit = Array.isArray(proposal.formData.registrations)
      ? proposal.formData.registrations.length > 0
        ? JSON.parse(JSON.stringify(proposal.formData.registrations))
        : this.getBlankRegistrations()
      : proposal.formData.registrations || this.getBlankRegistrations();

    // Convert fusion proposal data to the format expected by ProposalForm
    // This maps the fusion proposal structure back to the submission format
    const editProposalData = {
      // Core identification fields
      program: proposal.name,
      "CLIENT NAMES": proposal.clientName || "",
      "ADVISOR NAME": proposal.advisorName || "",

      // Template and branding fields
      customTemplate: proposal.customTemplate || "",
      LOGO: proposal.formData.logo || proposal.advisorName || "",
      logoSize: proposal.formData.logoSize || "",
      logoOffset: proposal.formData.logoOffset || "",

      // Risk and strategy fields - ensure empty string is treated as empty
      "embedded-excel":
        proposal.formData.riskTolerance &&
        proposal.formData.riskTolerance.trim() !== ""
          ? proposal.formData.riskTolerance
          : "",
      "insert-pdf": proposal.formData.snapShot || "",
      incomeNeeds: proposal.formData.incomeNeeds || 0,
      optionsTM: proposal.formData.optionsTM || "",

      // Document options
      papercheck:
        proposal.formData.papercheck !== undefined
          ? proposal.formData.papercheck
          : "true",
      othercheck: proposal.formData.othercheck || "",
      insertType: proposal.formData.insertType || "Fact Sheet",

      // Financial fields - use current fusion amounts
      assetValue: `$${proposal.amount.toLocaleString()}`,
      fusionPercentage: parseFloat(proposal.percentage) || 0,
      fusionAmount: proposal.amount || 0,

      // Registration data (deep copy to prevent shared references)
      registrations: registrationsForEdit,

      // Include fusion account numbers when copying
      fusionAccountNumbers: proposal.formData.fusionAccountNumbers
        ? JSON.parse(JSON.stringify(proposal.formData.fusionAccountNumbers))
        : [],

      // Set isNewFusionClient based on presence of account numbers
      isNewFusionClient:
        !proposal.formData.fusionAccountNumbers ||
        proposal.formData.fusionAccountNumbers.length === 0,

      // Clear timestamp for new copy
      DateTime: "",
    };

    // Prevent background scrolling
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = "17px"; // Account for scrollbar width

    this.setState({
      showProposalDialog: true,
      dialogFormRequestType: "Copy",
      dialogEditProposal: editProposalData,
      editIndex: -1, // Ensure copy creates a new proposal, doesn't edit existing
    });
  };

  // Update proposal amount
  updateProposalAmount = (proposalId, amount) => {
    const numericAmount = Math.round(parseFloat(amount) || 0);
    const percentage =
      this.state.totalFusionAmount > 0
        ? Math.round(
            (numericAmount / this.state.totalFusionAmount) * 100 * 100
          ) / 100
        : 0;

    // Validate program limits
    const proposal = this.state.selectedProposals.find(
      (p) => p.id === proposalId
    );
    const amountError = this.validateProposalAmount(proposal.name, numericAmount);

    this.setState(
      (prevState) => ({
        selectedProposals: prevState.selectedProposals.map((proposal) =>
          proposal.id === proposalId
            ? {
                ...proposal,
                amount: numericAmount,
                percentage: percentage,
                amountError: amountError,
              }
            : proposal
        ),
        errors: {
          ...prevState.errors,
          [`amount_${proposalId}`]: amountError,
        },
      }),
      this.calculateTotals
    );
  };

  // Update proposal percentage
  updateProposalPercentage = (proposalId, percentage) => {
    // Round percentage to 2 decimal places max
    const numericPercentage =
      Math.round((parseFloat(percentage) || 0) * 100) / 100;
    const amount =
      this.state.totalFusionAmount > 0
        ? Math.round((numericPercentage / 100) * this.state.totalFusionAmount)
        : 0;

    // Validate program limits
    const proposal = this.state.selectedProposals.find(
      (p) => p.id === proposalId
    );
    const amountError = this.validateProposalAmount(proposal.name, amount);

    this.setState(
      (prevState) => ({
        selectedProposals: prevState.selectedProposals.map((proposal) =>
          proposal.id === proposalId
            ? {
                ...proposal,
                percentage: numericPercentage,
                amount: amount,
                amountError: amountError,
              }
            : proposal
        ),
        errors: {
          ...prevState.errors,
          [`amount_${proposalId}`]: amountError,
        },
      }),
      this.calculateTotals
    );
  };

  // Get program limits from proposal types
  getProgramLimits = (programName) => {
    const { lists } = this.props;
    if (!lists || !lists.proposalTypes) return null;

    const program = lists.proposalTypes.find((p) => p.name === programName);
    // Use fusionLimits for fusion proposals, fallback to limits
    const limitsToUse = program?.fusionLimits || program?.limits;
    return limitsToUse
      ? {
          min: Number(limitsToUse.min),
          max: Number(limitsToUse.max),
        }
      : null;
  };

  // Calculate totals and validate
  calculateTotals = () => {
    const totalPercentage =
      Math.round(
        this.state.selectedProposals.reduce(
          (sum, proposal) => sum + (parseFloat(proposal.percentage) || 0),
          0
        ) * 100
      ) / 100;

    // Check for any amount validation errors
    const hasAmountErrors = this.state.selectedProposals.some(
      (proposal) => proposal.amountError
    );

    // Calculate current total amount
    const currentTotalAmount = this.state.selectedProposals.reduce(
      (sum, proposal) => sum + (Math.round(proposal.amount) || 0),
      0
    );

    // Check for zero amounts or percentages
    const hasZeroAmounts = this.state.selectedProposals.some(
      (proposal) => (proposal.amount || 0) <= 0
    );

    const hasZeroPercentages = this.state.selectedProposals.some(
      (proposal) => (parseFloat(proposal.percentage) || 0) <= 0
    );

    // Create combined zero values error message
    let zeroValuesError = null;
    if (hasZeroAmounts && hasZeroPercentages) {
      zeroValuesError =
        "All proposals must have amounts greater than $0 and percentages greater than 0%";
    } else if (hasZeroAmounts) {
      zeroValuesError = "All proposals must have amounts greater than $0";
    } else if (hasZeroPercentages) {
      zeroValuesError = "All proposals must have percentages greater than 0%";
    }

    // Validation based on amounts matching fusion total (more important than percentages)
    const amountsMatch =
      Math.abs(currentTotalAmount - this.state.totalFusionAmount) < 1;
    const isValid =
      amountsMatch &&
      this.state.selectedProposals.length >= 1 &&
      !hasAmountErrors &&
      !hasZeroAmounts &&
      !hasZeroPercentages &&
      this.getPendingInitialReviewCount() === 0;

    this.setState({
      totalPercentage: totalPercentage,
      isValid,
      errors: {
        ...this.state.errors,
        amounts: !amountsMatch
          ? `Total amounts ($${currentTotalAmount.toLocaleString()}) must equal fusion total ($${this.state.totalFusionAmount.toLocaleString()})`
          : null,
        zeroValues: zeroValuesError,
        initialReviews:
          this.getPendingInitialReviewCount() > 0
            ? `Review ${this.getPendingInitialReviewCount()} remaining ISP-seeded ${
                this.getPendingInitialReviewCount() === 1
                  ? "strategy"
                  : "strategies"
              } before submitting.`
            : null,
        riskTolerance: null, // Clear any previous risk tolerance errors
      },
    });
  };

  // Helper method to format currency for error messages
  formatCurrency = (amount) => {
    return `$${Math.round(amount).toLocaleString()}`;
  };

  // Get client name from form details (would integrate with App.js state)
  getClientNameFromFormDetails = () => {
    const { fusionStepGuideData } = this.props;
    return fusionStepGuideData?.clientName || "Client Name";
  };

  // Get advisor name from form details (would integrate with App.js state)
  getAdvisorNameFromFormDetails = () => {
    const { fusionStepGuideData } = this.props;
    return fusionStepGuideData?.advisorName || "Advisor Name";
  };

  // Get customTemplate from form details
  getCustomTemplateFromFormDetails = () => {
    const { fusionStepGuideData } = this.props;
    // customTemplate should be the advisor name (backend determines if they have custom templates)
    return (
      fusionStepGuideData?.customTemplate ||
      fusionStepGuideData?.advisorName ||
      ""
    );
  };

  // Get userName using centralized auth service
  getUserNameForSubmission = async () => {
    const { fusionStepGuideData } = this.props;

    // First try to get userName from fusionStepGuideData
    if (fusionStepGuideData?.userName) {
      return fusionStepGuideData.userName;
    }

    // Use authService which handles both Amplify and cookie fallback
    return await auth.getUserName();
  };

  // Helper function to normalize text for fuzzy matching
  normalizeText = (text) => {
    if (!text) return "";
    return text.toLowerCase().replace(/[^a-z0-9]/g, "");
  };

  // Get risk score for a proposal type
  getRiskScore = (proposalType, proposalTypeName) => {
    if (!proposalType || !proposalType.riskScore) return null;

    // Normalize the proposal name for matching
    const normalizedProposalName = this.normalizeText(proposalTypeName);

    // Try to find a matching risk score using normalized keys
    for (const [riskKey, riskValue] of Object.entries(proposalType.riskScore)) {
      const normalizedRiskKey = this.normalizeText(riskKey);
      if (normalizedRiskKey === normalizedProposalName) {
        return { key: riskKey, value: riskValue };
      }
    }

    // If no exact match, try partial matching
    for (const [riskKey, riskValue] of Object.entries(proposalType.riskScore)) {
      const normalizedRiskKey = this.normalizeText(riskKey);
      if (
        normalizedRiskKey.includes(normalizedProposalName) ||
        normalizedProposalName.includes(normalizedRiskKey)
      ) {
        return { key: riskKey, value: riskValue };
      }
    }

    return null;
  };

  // Calculate total weighted risk score by summing individual weighted contributions
  calculateWeightedRiskScore = () => {
    const { selectedProposals, totalFusionAmount } = this.state;
    let totalWeightedScore = 0;

    if (totalFusionAmount <= 0) return 0;

    selectedProposals.forEach((proposal) => {
      const amount = proposal.amount || 0;
      const riskTolerance = proposal.formData?.riskTolerance;

      if (amount > 0 && riskTolerance) {
        // Find matching proposal type for risk score
        const matchingType = this.props.proposalTypes?.find(
          (pt) => pt.name === proposal.name
        );

        if (matchingType) {
          const riskScoreData = this.getRiskScore(matchingType, riskTolerance);
          if (riskScoreData) {
            // Calculate this proposal's weighted contribution: riskScore × (amount ÷ totalAmount)
            const weightedContribution =
              riskScoreData.value * (amount / totalFusionAmount);
            totalWeightedScore += weightedContribution;
          }
        }
      }
    });

    return totalWeightedScore;
  };

  // Get risk tolerance category from score
  // Use Math.trunc to consider only the whole number (ignore decimals)
  // per IMR's original risk scoring methodology
  getRiskToleranceCategory = (score) => {
    const wholeScore = Math.trunc(score);
    if (wholeScore <= 35) return "Conservative";
    if (wholeScore <= 65) return "Moderate";
    return "Growth";
  };

  // Reset the fusion proposal and go back
  handleReset = () => {
    // Clear all fusion data
    this.setState({
      selectedProposals: [],
      totalPercentage: 0,
      isValid: false,
      errors: {},
      rebalanceType: "Automatic",
      showProposalDialog: false,
      ispSeedInitialized: false,
      seededFromISPWorkflow: false,
      originalISPStrategyNames: [],
    });

    // Navigate back to previous page
    if (this.props.history) {
      this.props.history.push("/proposals/step-guide");
    }
  };

  // Get PowerPoint folder name from proposal type data
  getPowerPointFolderName = (programName) => {
    if (!programName || !this.props.lists?.proposalTypes) return programName;

    // Find the matching proposal type
    const proposalType = this.props.lists.proposalTypes.find(
      (pt) => pt.name === programName
    );

    // Return the powerPoint field if available, otherwise return the original name
    return proposalType?.powerPoint || programName;
  };

  // Build fusion sections data for postData function (legacy format for backward compatibility)
  buildFusionSections = () => {
    if (this.state.selectedProposals.length === 0) return [];

    const sections = [];

    // Build _PLAN_BREAKDOWN_SECTION_ using legacy format
    // First, consolidate proposals by program name, risk tolerance, and optionsTM
    const programConsolidation = {};

    this.state.selectedProposals.forEach((proposal) => {
      const programName =
        this.getPowerPointFolderName(proposal.name)
          ?.toString()
          ?.replace("Proposal", "") ||
        proposal.name ||
        "";

      const riskTolerance = proposal.formData?.riskTolerance || "";
      const optionsTM = proposal.formData?.optionsTM || "";

      // Create a unique key combining program, risk tolerance, and optionsTM
      const consolidationKey = `${programName}|${riskTolerance}|${optionsTM}`;

      if (!programConsolidation[consolidationKey]) {
        programConsolidation[consolidationKey] = {
          program: programName,
          riskTolerance: riskTolerance,
          optionsTM: optionsTM,
          totalAmount: 0,
          proposals: [],
        };
      }

      // Add amount to total for this program combination
      programConsolidation[consolidationKey].totalAmount +=
        proposal.amount || 0;
      programConsolidation[consolidationKey].proposals.push(proposal);
    });

    // Calculate grand total for percentage calculations
    const grandTotal = Object.values(programConsolidation).reduce(
      (sum, prog) => sum + prog.totalAmount,
      0
    );

    // Build the plan breakdown data from consolidated programs
    // Sort by totalAmount (highest to lowest) before creating the data
    const sortedPrograms = Object.values(programConsolidation).sort(
      (a, b) => b.totalAmount - a.totalAmount
    );

    const planBreakdownSectionData = [];
    sortedPrograms.forEach((programData) => {
      const percentage =
        grandTotal > 0
          ? ((programData.totalAmount / grandTotal) * 100).toFixed(1)
          : "0";

      planBreakdownSectionData.push({
        name: "PlanBreakdownData",
        value: [
          {
            key: "PROGRAM",
            value: programData.program,
            type: "string",
          },
          {
            key: "RISK TOLERANCE",
            value: `${
              programData.riskTolerance !== null
                ? programData.riskTolerance
                    ?.replace("Taxable", "")
                    .replace("Municipal", "")
                    .trim()
                : ""
            }${
              programData.optionsTM !== null && programData.optionsTM !== ""
                ? ` ${programData.optionsTM}`
                : ""
            }`,
            type: "string",
          },
          {
            key: "AMOUNT",
            value: this.formatCurrency(programData.totalAmount) || "",
            type: "string",
          },
          {
            key: "PERCENTAGE",
            value: `${percentage}%`,
            type: "string",
          },
        ],
      });
    });

    const planBreakdownSection = {
      key: "PLAN BREAKDOWN SECTION",
      value: JSON.stringify(planBreakdownSectionData),
      type: "sections",
    };
    sections.push(planBreakdownSection);

    // Group proposals by program name and calculate total allocation per program
    // Track original order to maintain stability within groups
    const programGroups = {};
    this.state.selectedProposals.forEach((proposal, originalIndex) => {
      const programName =
        proposal.name || this.getPowerPointFolderName(proposal.name) || "";
      if (!programGroups[programName]) {
        programGroups[programName] = {
          proposals: [],
          totalAmount: 0,
          firstIndex: originalIndex, // Track first appearance for stable sorting
        };
      }
      programGroups[programName].proposals.push(proposal);
      programGroups[programName].totalAmount += proposal.amount || 0;
    });

    // Sort groups by total allocation (highest to lowest)
    // Within each group, proposals maintain their original order
    const sortedProposals = Object.entries(programGroups)
      .sort(([, groupA], [, groupB]) => {
        // Primary sort: by total amount (highest first)
        const amountDiff = groupB.totalAmount - groupA.totalAmount;
        if (amountDiff !== 0) return amountDiff;
        // Secondary sort: maintain original order by first index
        return groupA.firstIndex - groupB.firstIndex;
      })
      .flatMap(([, group]) => group.proposals);

    // Build combined _RISK_IMAGE_SECTION_ containing all proposals in one array
    let allRiskImageSectionData = [];
    sortedProposals.forEach((proposal, index) => {
      let uuid = uuidv4();
      const riskImageData = [
        {
          name: "RiskImageClientName",
          value: {
            key: "CLIENT NAME",
            value: proposal.clientName || "",
            type: "string",
          },
        },
        {
          name: "RiskImageRegistration",
          value: {
            key: "REGISTRATION",
            value:
              proposal.formData?.registrations &&
              proposal.formData?.registrations.length > 1
                ? JSON.stringify(proposal.formData?.registrations)
                : proposal.formData?.registrations &&
                  proposal.formData?.registrations[0]
                ? proposal.formData?.registrations[0].value ||
                  proposal.formData?.registrations[0]
                : "",
            type: "string",
          },
        },
        {
          name: "RiskImageProgram",
          value: {
            key: "PROGRAM",
            value:
              proposal.name ||
              this.getPowerPointFolderName(proposal.name) ||
              "",
            type: "string",
          },
        },
        {
          name: "RiskImageRiskTolerance",
          value: {
            key: "RISK TOLERANCE",
            value: `${
              proposal.formData?.riskTolerance !== null
                ? proposal.formData?.riskTolerance
                    ?.replace("Taxable", "")
                    .replace("Municipal", "")
                    .trim()
                : ""
            }${
              proposal.formData?.optionsTM !== null &&
              proposal.formData?.optionsTM !== ""
                ? ` ${proposal.formData?.optionsTM}`
                : ""
            }`,
            type: "string",
          },
        },
        {
          name: "RiskImageDisclosure",
          value: {
            key: "DISCLOSURE",
            value: "",
            type: "string",
          },
        },
        {
          name: "RiskImageRiskImage",
          value: {
            key: "RISK IMAGE",
            value: JSON.stringify({
              riskTolerance: proposal.formData?.riskTolerance || "",
              powerPoint: proposal.formData?.powerPoint || "",
              logo: proposal.formData?.logo || "",
              optionsTM: proposal.formData?.optionsTM || "",
              factSheet: proposal.formData?.paperwork?.factSheet || [],
              uuid: uuid,
              scale: 90,
            }),
            type: "image",
            center: true, // Hard coded as specified
          },
        },
        // dont add page break after last proposal
        sortedProposals.length - 1 === index
          ? null
          : {
              name: "pageBreak",
              value: {
                key: "pageBreak",
                value: "",
                type: "pageBreak",
              },
            },
      ];

      // Filter out any null values (e.g., pageBreak for last proposal)
      allRiskImageSectionData = allRiskImageSectionData.concat(
        riskImageData.filter(Boolean)
      );

      // Skip insert-pdf for Elements (snapshots removed from system)
      if (!proposal.name?.toLowerCase().includes("elements")) {
        // Check if insert-pdf section already exists (matching all fields except uuid)
        const insertPdfData = {
          optionsTM: proposal.formData?.optionsTM || "",
          riskTolerance: proposal.formData?.riskTolerance || "",
          powerPoint: proposal.formData?.powerPoint,
          factSheet: proposal.formData?.paperwork?.factSheet || [],
        };

        const existingInsertPdfIndex = sections.findIndex((section) => {
          if (section.key !== "insert-pdf") return false;
          try {
            const existingData = JSON.parse(section.value);
            // Compare all fields except uuid
            return (
              section.type ===
                (proposal.formData?.insertType || "Fact Sheet") &&
              existingData.optionsTM === insertPdfData.optionsTM &&
              existingData.riskTolerance === insertPdfData.riskTolerance &&
              existingData.powerPoint === insertPdfData.powerPoint &&
              JSON.stringify(existingData.factSheet) ===
                JSON.stringify(insertPdfData.factSheet)
            );
          } catch {
            return false;
          }
        });

        if (existingInsertPdfIndex >= 0) {
          // Update existing insert-pdf with new uuid
          sections[existingInsertPdfIndex] = {
            key: "insert-pdf",
            value: JSON.stringify({
              ...insertPdfData,
              uuid: uuid,
            }),
            type: proposal.formData?.insertType || "Fact Sheet",
          };
        } else {
          // Add new insert-pdf section
          sections.push({
            key: "insert-pdf",
            value: JSON.stringify({
              ...insertPdfData,
              uuid: uuid,
            }),
            type: proposal.formData?.insertType || "Fact Sheet",
          });
        }
      }
    });

    // Create single RISK IMAGE SECTION containing all proposals
    const riskImageSection = {
      key: "RISK IMAGE SECTION",
      value: JSON.stringify(allRiskImageSectionData),
      type: "sections",
    };
    sections.push(riskImageSection);

    // Consolidate pie chart data by program name
    const programTotals = {};

    // First, sum amounts by program name
    this.state.selectedProposals.forEach((proposal) => {
      const programName =
        proposal.name ??
        this.getPowerPointFolderName(proposal.name)
          ?.toString()
          ?.replace("Proposal", "") ??
        "";

      if (!programTotals[programName]) {
        programTotals[programName] = 0;
      }

      programTotals[programName] += proposal.amount || 0;
    });

    // Calculate grand total for pie chart
    const donutChartGrandTotal = Object.values(programTotals).reduce(
      (sum, amount) => sum + amount,
      0
    );

    // Build pie chart data with consolidated percentages
    // Sort entries by totalAmount (highest to lowest)
    const sortedEntries = Object.entries(programTotals).sort(
      ([, amountA], [, amountB]) => amountB - amountA
    );

    let donutChartData = {};
    sortedEntries.forEach(([programName, totalAmount]) => {
      const percentage =
        donutChartGrandTotal > 0
          ? ((totalAmount / donutChartGrandTotal) * 100).toFixed(1)
          : "0";
      donutChartData[programName] = percentage;
    });

    const donutChart = {
      key: "DONUT CHART",
      value: JSON.stringify(donutChartData),
      type: "donutchart",
    };
    sections.push(donutChart);

    // Build _SIGNATURE_SECTION_ with combined client info and strategy table
    const accountNumbers = this.getAccountNumbers(); // Will need to implement this

    // Build table data with alternating white/gray rows
    const tableRows = [];
    sortedProposals.forEach((proposal, index) => {
      const isWhite = index % 2 === 0;
      const rowName = isWhite ? "SignatureDataWhite" : "SignatureDataGray";

      tableRows.push({
        name: rowName,
        value: [
          {
            key: "CLIENT NAME",
            value: proposal.clientName || "",
            type: "string",
          },
          {
            key: "REGISTRATION",
            value:
              proposal.formData?.registrations &&
              proposal.formData?.registrations.length > 1
                ? JSON.stringify(proposal.formData?.registrations)
                : proposal.formData?.registrations &&
                  proposal.formData?.registrations[0]
                ? proposal.formData?.registrations[0].value ||
                  proposal.formData?.registrations[0]
                : "",
            type: "string",
          },
          {
            key: "PROGRAM",
            value:
              this.getPowerPointFolderName(proposal.name)
                ?.toString()
                ?.replace("Proposal", "") ||
              proposal.name ||
              "",
            type: "string",
          },
          {
            key: "RISK TOLERANCE",
            value: `${
              proposal.formData?.riskTolerance !== null
                ? proposal.formData?.riskTolerance
                    ?.replace("Taxable", "")
                    .replace("Municipal", "")
                    .trim()
                : ""
            }${
              proposal.formData?.optionsTM !== null &&
              proposal.formData?.optionsTM !== ""
                ? ` ${proposal.formData?.optionsTM}`
                : ""
            }`,
            type: "string",
          },
          {
            key: "VALUE",
            value: `$${Math.round(proposal.amount).toLocaleString()}`,
            type: "string",
          },
          {
            key: "PERCENTAGE",
            value: `${proposal.percentage}%` || "",
            type: "string",
          },
        ],
      });
    });

    const signatureSection = {
      key: "SIGNATURE SECTION",
      value: JSON.stringify(
        [
          accountNumbers.length > 0
            ? {
                name: "SignatureExistingAccounts",
                value: {
                  key: "ACCOUNT NUMBERS",
                  value: accountNumbers.join(", "),
                  type: "string",
                },
              }
            : null,
          {
            name: "SignatureRebalance",
            value: {
              key: "REBALANCE",
              value: `Rebalance Type: ${this.state.rebalanceType}`,
              type: "string",
            },
          },
          {
            name: "SignatureTableHeader",
            value: {
              key: "TABLE HEADER",
              value: "", // No text replacements for table header
              type: "string",
            },
          },
          ...tableRows, // Add all the data rows
        ].filter(Boolean)
      ),
      type: "sections",
    };

    if (this.state.selectedProposals.length < 5) {
      sections.push({
        key: "CHART SIGNATURE",
        value: donutChart.value,
        type: "donutchart",
      });
    }

    sections.push(signatureSection);

    return sections;
  };

  // Helper method to get account numbers from form data
  getAccountNumbers = () => {
    const { fusionStepGuideData } = this.props;
    // Use fusionAccountNumbers from the form data
    return fusionStepGuideData?.fusionAccountNumbers || [];
  };

  // Submit the fusion proposal
  handleSubmit = async () => {
    if (!this.state.isValid) return;

    // Use standard fusion sections format compatible with your backend structure
    const fusionSectionData = this.buildFusionSections();

    // Note: fusionSummary data available if needed for enhanced display

    // Calculate overall fusion risk tolerance using existing weighted risk calculation
    const calculateFusionRiskTolerance = () => {
      if (this.state.selectedProposals.length === 0) return "";

      // Use the existing weighted risk score calculation
      const weightedRiskScore = this.calculateWeightedRiskScore();

      if (weightedRiskScore === null || isNaN(weightedRiskScore)) return "";

      // Convert weighted risk score to risk tolerance category
      // Use Math.trunc to consider only the whole number (ignore decimals)
      // per IMR's original risk scoring methodology
      // Conservative: 1-35, Moderate: 36-65, Growth: 66-100
      const wholeScore = Math.trunc(weightedRiskScore);
      if (wholeScore <= 35) return "Conservative";
      if (wholeScore <= 65) return "Moderate";
      return "Growth";
    };

    const getFusionBondValue = () => {
      for (let i = 0; i < this.state.selectedProposals.length; i++) {
        const proposal = this.state.selectedProposals[i];
        const optionsTM = proposal.formData?.optionsTM;
        if (optionsTM === "Municipal") {
          return "Municipal";
        }
      }

      return "Taxable"; // Default to Taxable if no selections
    };

    // Calculate total income needs by tallying up all proposal income values
    const calculateFusionIncomeNeeds = () => {
      if (this.state.selectedProposals.length === 0) return 0;

      let totalIncome = 0;

      // Sum up income needs from all proposals
      this.state.selectedProposals.forEach((proposal) => {
        const incomeNeeds = proposal.formData?.incomeNeeds;
        if (incomeNeeds) {
          // Convert to number, handling string values with currency symbols
          const incomeValue =
            parseFloat(String(incomeNeeds).replace(/[^0-9.-]+/g, "")) || 0;
          totalIncome += incomeValue;
        }
      });

      return totalIncome;
    };

    // Calculate fusion registration value based on selected proposals
    const calculateFusionRegistration = () => {
      if (this.state.selectedProposals.length === 0) return "";

      // Collect all unique registration values from selected proposals
      const registrationValues = new Set();

      this.state.selectedProposals.forEach((proposal) => {
        if (proposal.formData?.registrations) {
          if (Array.isArray(proposal.formData.registrations)) {
            // Handle array format: [{registration: "registration0", value: "IRA", percentage: "100"}]
            const firstReg = proposal.formData.registrations[0];
            if (firstReg?.value) {
              registrationValues.add(firstReg.value);
            }
          } else if (typeof proposal.formData.registrations === "string") {
            // Handle string format directly
            registrationValues.add(proposal.formData.registrations);
          }
        }
      });

      // Return format compatible with proposalConfirm component
      if (registrationValues.size === 0) return "";
      if (registrationValues.size === 1) {
        // Return as single-item array with value property for consistent format
        return [{ value: Array.from(registrationValues)[0] }];
      }
      // Return "Multiple" wrapped in array format to be consistent with proposalConfirm expectations
      // This will display as "Multiple" in the confirmation page
      return [{ value: "Multiple" }];
    };

    // Get userName from authenticated user
    const userName = await this.getUserNameForSubmission();

    // Create values object for confirmation page using fusion step guide data
    const fusionValues = {
      ...this.props.fusionStepGuideData,
      program: this.props.fusionStepGuideData?.program || "Fusion",
      userName: userName,
      clientName: this.getClientNameFromFormDetails(),
      advisorName: this.getAdvisorNameFromFormDetails(),
      customTemplate: this.getCustomTemplateFromFormDetails(), // Include customTemplate for backend processing
      assetValue: `$${Math.round(
        this.state.totalFusionAmount
      ).toLocaleString()}`,
      riskTolerance: calculateFusionRiskTolerance(),
      optionsTM: getFusionBondValue(),
      incomeNeeds: calculateFusionIncomeNeeds(), // Tallied income needs from all proposals
      insertType: "Fusion",
      registrations: calculateFusionRegistration(), // Set based on selected proposals
    };

    // Store confirmation data and show confirmation page
    this.setState({
      showConfirmation: true,
      confirmationData: {
        values: fusionValues,
        fusionSections: {
          data: {
            sections: fusionSectionData,
          },
        },
        fusionSummary: {
          rebalanceType: this.state.rebalanceType, // Only pass what can't be calculated
        },
        selectedProposals: this.state.selectedProposals,
      },
    });
  };

  // Handle back from confirmation
  handleBackFromConfirmation = () => {
    this.setState({
      showConfirmation: false,
      confirmationData: null,
    });
  };

  // Render the proposal selection dialog
  renderProposalDialog = () => {
    if (!this.state.showProposalDialog) return null;

    return (
      <div
        className="modal fade"
        tabIndex="-1"
        style={{ display: "contents", overflow: "visible" }}
      >
        <div
          className="modal-dialog modal-lg modal-dialog-centered"
          role="document"
          style={{
            zIndex: 1060,
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            margin: 0,
            maxHeight: "85vh",
            minWidth: "33vw",
            padding: "0px",
          }}
        >
          <div
            className="modal-content"
            style={{ maxHeight: "85vh", overflowY: "auto" }}
          >
            <div className="modal-header">
              <h5 className="modal-title vicus-blue">
                {this.state.dialogFormRequestType === "Edit"
                  ? "Edit Strategy in Fusion"
                  : this.state.dialogFormRequestType === "Copy"
                  ? "Copy Strategy to Fusion"
                  : "Add Strategy to Fusion"}
              </h5>
              <button
                type="button"
                className="close"
                onClick={this.hideProposalDialog}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div
              className="modal-body"
              style={{
                paddingRight: "30px",
                paddingLeft: "30px",
                paddingBottom: "20px",
              }}
            >
              <ProposalForm
                ref={this.proposalFormRef}
                lists={this.props.lists}
                fusion={true}
                formRequestType={this.state.dialogFormRequestType}
                editProposal={this.state.dialogEditProposal}
                loggedIn={this.props.loggedIn}
                totalFusionAmount={this.state.totalFusionAmount}
                currentFusionTotal={this.state.selectedProposals.reduce(
                  (sum, p) => sum + (p.amount || 0),
                  0
                )}
                fusionProposals={this.state.selectedProposals}
                editIndex={this.state.editIndex}
                addToFusion={this.addProposal}
                fusionStepGuideData={this.props.fusionStepGuideData}
              />
            </div>
          </div>
        </div>
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 1050,
            width: "100vw",
            height: "100vh",
            backgroundColor: "#000",
            opacity: 0.5,
          }}
          onClick={this.hideProposalDialog}
        />
      </div>
    );
  };

  render() {
    const {
      rebalanceType,
      selectedProposals,
      totalFusionAmount,
      totalPercentage,
      isValid,
      errors,
      showConfirmation,
      confirmationData,
      showSuccess,
    } = this.state;
    const reviewMode = this.isInInitialReviewMode();
    const divergenceWarnings = this.getStrategyDivergenceWarnings();
    const ispRiskCategory = this.getISPReferenceRiskCategory();
    const ispSelectedPortfolio = this.getISPReferenceSelectedPortfolio();

    // Show confirmation page if submitted
    if (showConfirmation && confirmationData) {
      return (
        <div>
          <div
            style={{
              height: "calc(100vh - 50px)",
              position: "fixed",
              width: "100vw",
              backgroundColor: "#A2AAB6",
            }}
          />
          <div className="container">
            <div style={{ paddingTop: "35px" }}>
              <div className="card">
                <div className="card-body">
                  <ProposalConfirm
                    values={confirmationData.values}
                    lists={this.props.lists}
                    fusionSections={confirmationData.fusionSections}
                    fusionSummary={confirmationData.fusionSummary}
                    fusionProposals={confirmationData.selectedProposals}
                    handles={{
                      handleDefault: () => {}, // Mock function to prevent errors
                    }}
                    prevStep={this.handleBackFromConfirmation}
                    nextStep={() => {
                      // Show success screen after successful submission
                      this.setState({
                        showConfirmation: false,
                        showSuccess: true,
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Show success page after successful submission
    if (showSuccess) {
      return (
        <div>
          <div
            style={{
              height: "calc(100vh - 50px)",
              position: "fixed",
              width: "100vw",
              backgroundColor: "#A2AAB6",
            }}
          />
          <div className="container">
            <div style={{ paddingTop: "35px" }}>
              <div className="card">
                <div className="card-body">
                  <ProposalSuccess
                    proposals={this.props.lists.proposals}
                    proposalType={confirmationData?.proposalType || "Fusion"}
                    updateProposalType={() => {}}
                    values={confirmationData?.values || {}}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div
          style={{
            height: "calc(100vh - 50px)",
            position: "fixed",
            width: "100vw",
            backgroundColor: "#A2AAB6",
          }}
        />
        <div className="container">
          <div style={{ paddingTop: "35px" }}>
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h1 className="vicus-blue mb-0">Fusion Proposal:</h1>
                  <button
                    className="btn btn-secondary"
                    onClick={this.handleReset}
                    title="Reset fusion proposal and go back to edit details"
                  >
                    Reset
                  </button>
                </div>

                {/* Fusion Total Amount */}
                {totalFusionAmount > 0 && (
                  <div className="alert alert-info mb-2">
                    <strong>Fusion Total Amount:</strong> $
                    {totalFusionAmount.toLocaleString()}
                    <br />
                    <strong>Advisor:</strong>{" "}
                    {this.getAdvisorNameFromFormDetails()}
                    {(ispRiskCategory || ispSelectedPortfolio) && (
                      <>
                        <br />
                        {ispRiskCategory && (
                          <>
                            <strong>ISP Risk Category:</strong> {ispRiskCategory}
                          </>
                        )}
                        {ispRiskCategory && ispSelectedPortfolio && <br />}
                        {ispSelectedPortfolio && (
                          <>
                            <strong>ISP Selected Portfolio:</strong>{" "}
                            {ispSelectedPortfolio}
                          </>
                        )}
                      </>
                    )}
                    <br />
                    <small className="text-muted">
                      This is the total amount from your initial fusion proposal
                      setup.
                    </small>
                  </div>
                )}

                {/* Rebalance Type Selection */}
                <div className="form-group mb-2">
                  <label>
                    <strong>Rebalance Type</strong>
                  </label>
                  <div className="mt-2">
                    <div className="form-check form-check-inline">
                      <input
                        style={{ height: "15px", width: "15px" }}
                        className="form-check-input"
                        type="radio"
                        name="rebalanceType"
                        id="automaticRadio"
                        value="Automatic"
                        checked={rebalanceType === "Automatic"}
                        disabled={this.checkForceManualRebalance(
                          selectedProposals
                        )}
                        onChange={() =>
                          this.handleRebalanceTypeChange("Automatic")
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="automaticRadio"
                      >
                        Automatic
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        style={{ height: "15px", width: "15px" }}
                        className="form-check-input"
                        type="radio"
                        name="rebalanceType"
                        id="manualRadio"
                        value="Manual"
                        checked={rebalanceType === "Manual"}
                        disabled={this.checkForceManualRebalance(
                          selectedProposals
                        )}
                        onChange={() =>
                          this.handleRebalanceTypeChange("Manual")
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="manualRadio"
                      >
                        Manual
                      </label>
                    </div>
                    {this.checkForceManualRebalance(selectedProposals) && (
                      <small className="text-muted d-block mt-2">
                        <i className="fa fa-info-circle mr-1"></i>
                        Rebalance type is automatically set to "Manual" when
                        proposals have different client names or different
                        registration types.
                      </small>
                    )}
                  </div>
                </div>

                {/* Proposal List Management */}
                <div className="form-group mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <label>
                      <strong>
                        Strategies in Fusion ({selectedProposals.length}/8)
                      </strong>
                    </label>
                    {!reviewMode && (
                      <button
                        className="btn btn-primary"
                        style={{ marginRight: "5px" }}
                        onClick={this.showProposalDialog}
                        disabled={selectedProposals.length >= 8}
                      >
                        <i className="fa fa-plus mr-2"></i>
                        Add Strategy
                      </button>
                    )}
                  </div>

                  {/* Error Messages */}
                  {errors.maxProposals && (
                    <div className="alert alert-warning">
                      {errors.maxProposals}
                    </div>
                  )}

                  {errors.initialReviews && (
                    <div className="alert alert-warning">
                      <i className="fa fa-clipboard-check mr-2"></i>
                      {errors.initialReviews}
                    </div>
                  )}

                  {divergenceWarnings.length > 0 && (
                    <div className="alert alert-warning mt-2">
                      <i className="fa fa-info-circle mr-2"></i>
                      This Fusion now includes strategies that were not in the original ISP:
                      {" "}
                      {divergenceWarnings.join(", ")}.
                    </div>
                  )}

                  {/* Selected Proposals List */}
                  {selectedProposals.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <p>No strategies added yet.</p>
                      <p>Click "Add Strategy" to get started.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th style={{ width: "30%", minWidth: "200px" }}>
                              Strategy
                            </th>
                            <th style={{ width: "30%", minWidth: "150px" }}>
                              Client Name
                            </th>
                            <th style={{ width: "15%", minWidth: "100px" }}>
                              Amount ($)
                            </th>
                            <th style={{ width: "15%", minWidth: "100px" }}>
                              Percentage (%)
                            </th>
                            <th style={{ width: "10%", minWidth: "80px" }}>
                              {reviewMode ? "Review" : "Actions"}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedProposals.map((proposal, index) => (
                            <tr key={proposal.id}>
                              <td
                                style={{
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                <strong>{proposal.name}</strong>
                                <br />
                                <small className="text-muted">
                                  {(() => {
                                    // Extract registration information from proposal
                                    if (
                                      proposal.formData?.registrations &&
                                      Array.isArray(
                                        proposal.formData.registrations
                                      )
                                    ) {
                                      const firstReg =
                                        proposal.formData.registrations[0];
                                      if (firstReg?.value) {
                                        return firstReg.value;
                                      }
                                    }
                                    return "";
                                  })()}
                                </small>
                              </td>
                              <td
                                style={{
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                <strong>{proposal.clientName}</strong>
                                <br />
                                <small className="text-muted">
                                  {(() => {
                                    const matchingType =
                                      this.props.proposalTypes?.find(
                                        (pt) => pt.name === proposal.name
                                      );
                                    const riskTolerance =
                                      proposal.formData?.riskTolerance || "N/A";
                                    const riskScoreData =
                                      matchingType && riskTolerance !== "N/A"
                                        ? this.getRiskScore(
                                            matchingType,
                                            riskTolerance
                                          )
                                        : null;

                                    if (
                                      riskScoreData &&
                                      proposal.amount &&
                                      this.state.totalFusionAmount > 0
                                    ) {
                                      // Calculate weighted risk score: riskScore × (proposalAmount ÷ totalAmount)
                                      const weightedScore =
                                        riskScoreData.value *
                                        (proposal.amount /
                                          this.state.totalFusionAmount);
                                      return `${riskTolerance} (${
                                        Math.round(weightedScore * 100) / 100
                                      })`;
                                    }

                                    return riskTolerance;
                                  })()}
                                </small>
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className={`form-control form-control-sm ${
                                    proposal.amountError ? "is-invalid" : ""
                                  }`}
                                  disabled={reviewMode}
                                  value={
                                    proposal.amount
                                      ? `$${proposal.amount.toLocaleString()}`
                                      : ""
                                  }
                                  onFocus={(e) => {
                                    // Remove formatting on focus for editing
                                    e.target.value = proposal.amount || "";
                                  }}
                                  onBlur={(e) => {
                                    // Restore formatting on blur
                                    const value = e.target.value.replace(
                                      /[^0-9.]/g,
                                      ""
                                    );
                                    if (value) {
                                      e.target.value = `$${parseFloat(
                                        value
                                      ).toLocaleString()}`;
                                    }
                                  }}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(
                                      /[^0-9.]/g,
                                      ""
                                    );
                                    this.updateProposalAmount(
                                      proposal.id,
                                      value
                                    );
                                  }}
                                  placeholder="$0"
                                />
                                {proposal.amountError && (
                                  <div className="invalid-feedback">
                                    {proposal.amountError}
                                  </div>
                                )}
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className={`form-control form-control-sm ${
                                    proposal.amountError ? "is-invalid" : ""
                                  }`}
                                  disabled={reviewMode}
                                  value={proposal.percentage || ""}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(
                                      /[^0-9.]/g,
                                      ""
                                    );
                                    this.updateProposalPercentage(
                                      proposal.id,
                                      value
                                    );
                                  }}
                                  placeholder="0.00"
                                />
                                {proposal.amountError && (
                                  <div className="invalid-feedback">
                                    {proposal.amountError}
                                  </div>
                                )}
                              </td>
                              <td>
                                {reviewMode ? (
                                  <button
                                    className={`btn btn-sm ${
                                      proposal.initialReviewComplete
                                        ? "btn-success"
                                        : "btn-primary"
                                    }`}
                                    style={{ minWidth: "120px" }}
                                    onClick={() => this.editProposal(index)}
                                    title={
                                      proposal.initialReviewComplete
                                        ? "Edit reviewed strategy"
                                        : "Review strategy"
                                    }
                                  >
                                    {proposal.initialReviewComplete
                                      ? "Review Complete"
                                      : "Review Strategy"}
                                  </button>
                                ) : (
                                  <div className="d-flex justify-content-center">
                                    <button
                                      className="btn btn-sm me-1"
                                      style={{
                                        backgroundColor: "#28a745",
                                        color: "white",
                                        borderColor: "white",
                                        marginRight: "5px",
                                        padding: "4px 8px",
                                      }}
                                      onClick={() => this.editProposal(index)}
                                      title="Edit proposal"
                                    >
                                      <i
                                        className="fa fa-pencil-square-o"
                                        style={{ fontSize: "12px" }}
                                      ></i>
                                    </button>
                                    <button
                                      className="btn btn-sm me-1"
                                      style={{
                                        backgroundColor: "#385EA4",
                                        color: "white",
                                        borderColor: "white",
                                        marginRight: "5px",
                                        padding: "4px 8px",
                                      }}
                                      onClick={() => this.copyProposal(proposal)}
                                      title="Copy proposal and add a new"
                                    >
                                      <i
                                        className="fa fa-copy"
                                        style={{ fontSize: "12px" }}
                                      ></i>
                                    </button>
                                    <button
                                      className="btn btn-sm"
                                      style={{
                                        backgroundColor: "#385EA4",
                                        color: "white",
                                        borderColor: "white",
                                        padding: "4px 8px",
                                      }}
                                      onClick={() =>
                                        this.removeProposal(proposal.id)
                                      }
                                      title="Remove proposal"
                                    >
                                      <i
                                        className="fa fa-times"
                                        style={{ fontSize: "12px" }}
                                      ></i>
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="font-weight-bold">
                            <td>Total</td>
                            <td>
                              Weighted Risk Score:{" "}
                              {Math.round(
                                this.calculateWeightedRiskScore() * 100
                              ) / 100}
                              <br />(
                              {this.calculateWeightedRiskScore() > 0
                                ? this.getRiskToleranceCategory(
                                    this.calculateWeightedRiskScore()
                                  )
                                : "N/A"}
                              )
                            </td>
                            <td
                              className={
                                selectedProposals.reduce(
                                  (sum, p) => sum + Math.round(p.amount || 0),
                                  0
                                ) === 0
                                  ? ""
                                  : Math.abs(
                                      selectedProposals.reduce(
                                        (sum, p) =>
                                          sum + Math.round(p.amount || 0),
                                        0
                                      ) - totalFusionAmount
                                    ) < 1
                                  ? "text-success"
                                  : "text-warning"
                              }
                            >
                              $
                              {Math.round(
                                selectedProposals.reduce(
                                  (sum, p) => sum + Math.round(p.amount || 0),
                                  0
                                )
                              ).toLocaleString()}
                            </td>
                            <td
                              className={
                                totalPercentage === 0
                                  ? ""
                                  : Math.abs(totalPercentage - 100) < 0.01
                                  ? "text-success"
                                  : "text-warning"
                              }
                            >
                              {(
                                Math.round(totalPercentage * 100) / 100
                              ).toFixed(2)}
                              %
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}

                  {/* Validation Messages */}
                  {errors.amounts && (
                    <div className="alert alert-warning mt-2">
                      <i className="fa fa-exclamation-triangle mr-2"></i>
                      {errors.amounts}
                    </div>
                  )}

                  {errors.zeroValues && (
                    <div className="alert alert-warning mt-2">
                      <i className="fa fa-exclamation-triangle mr-2"></i>
                      {errors.zeroValues}
                    </div>
                  )}

                  {/* Show validation errors for program limits */}
                  {selectedProposals.some((p) => p.amountError) && (
                    <div className="alert alert-warning mt-2">
                      <i className="fa fa-exclamation-triangle mr-2"></i>
                      Some proposals have amount validation errors. Please check
                      the minimum and maximum limits for each strategy.
                    </div>
                  )}

                  {/* Sales contact note for errors/warnings */}
                  {(errors.amounts ||
                    errors.zeroValues ||
                    selectedProposals.some((p) => p.amountError)) && (
                    <div className="alert alert-info mt-1">
                      <i className="fa fa-info-circle mr-1"></i>
                      We understand these constraints may not fit every
                      scenario. If you need more flexibility or customization
                      options for your proposal, our Advisor Engagement team is
                      happy to help explore additional solutions that might work
                      better for your needs.
                    </div>
                  )}

                  {Math.abs(
                    selectedProposals.reduce(
                      (sum, p) => sum + Math.round(p.amount || 0),
                      0
                    ) - totalFusionAmount
                  ) < 1 &&
                    selectedProposals.length > 0 &&
                    !reviewMode &&
                    !selectedProposals.some((p) => p.amountError) && (
                      <div className="alert alert-success mt-2">
                        <i className="fa fa-check mr-2"></i>
                        Ready to submit! Total allocation matches reference
                        amount and all amounts are valid.
                      </div>
                    )}
                </div>

                {/* Submit Button */}
                <div className="text-right">
                  <button
                    className="btn btn-success"
                    onClick={this.handleSubmit}
                    disabled={!isValid}
                  >
                    Submit Fusion Proposal
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Alert Dialog */}
        {this.state.showAlertDialog && (
          <>
            <div
              className="modal-backdrop fade show"
              onClick={this.hideAlert}
            ></div>
            <div className="modal fade show" style={{ display: "block" }}>
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{this.state.alertTitle}</h5>
                    <button
                      type="button"
                      className="close"
                      onClick={this.hideAlert}
                    >
                      <span>&times;</span>
                    </button>
                  </div>
                  <div className="modal-body">
                    <p style={{ whiteSpace: "pre-line" }}>
                      {this.state.alertMessage}
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={this.hideAlert}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Confirm Dialog */}
        {this.state.showConfirmDialog && (
          <>
            <div
              className="modal-backdrop fade show"
              onClick={this.hideConfirm}
            ></div>
            <div className="modal fade show" style={{ display: "block" }}>
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{this.state.confirmTitle}</h5>
                    <button
                      type="button"
                      className="close"
                      onClick={this.hideConfirm}
                    >
                      <span>&times;</span>
                    </button>
                  </div>
                  <div className="modal-body">
                    <p style={{ whiteSpace: "pre-line" }}>
                      {this.state.confirmMessage}
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={this.hideConfirm}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={this.handleConfirmYes}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Proposal Selection Dialog */}
        {this.renderProposalDialog()}
      </div>
    );
  }
}

export default FusionProposal;
